import { useSupportSheet } from "../../components/SupportSheet";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AuraLayout, Button, Layout, SupportIcon } from "lola-framework-ui-test";
import { Toast } from "../../components/Toast";
import {
  GALLERY_NOTICE_FIXTURES,
  GalleryInboxChrome,
  GalleryInboxDrawer,
  GalleryPreferencesSheet,
} from "../../components/notifications/GalleryInbox";
import { TransactionsHistory } from "../../components/deferred-origination/TransactionsHistory";
import { DEFERRED_ORIGINATION_ROUTES } from "../../constants";
import { useStore } from "../../store/globalState";
import { deferredOriginationMockList } from "../../gallery/deferredOriginationMock";
import { useGalleryCashDepositLaunch } from "../../hooks/useGalleryCashDepositLaunch";
import type { ITransaction } from "../../interfaces/transactions";
import { BodyCopy } from "../../components/BodyCopy";
import { Navbar } from "../../components/Navbar";

/**
 * Deferred payout sender hub. Lists originations created from this link.
 *
 * Parity with production: navbar Back returns to Landing. "Back home" is omitted
 * in the gallery (no partner exit URL), matching hubs without callbackUrl.
 */
interface HomePageProps {
  originationDisabled?: boolean;
  /** Gallery inspectable: freeze the pending-cash row in face-sweep launch. */
  depositLaunching?: boolean;
  inboxOpen?: boolean;
  toastOpen?: boolean;
  /** Gallery inspectable: open the preferences sheet on first paint. */
  preferencesOpen?: boolean;
}

export const HomePage = memo(({
  originationDisabled = false,
  depositLaunching = false,
  inboxOpen = false,
  toastOpen = false,
  preferencesOpen: preferencesOpenProp = false,
}: HomePageProps) => {
  const { t } = useTranslation("deferred-origination");
  const { theme } = useStore();
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("deferred-origination");
  const { launch: launchCashDeposit, payingOperationId } =
    useGalleryCashDepositLaunch({ frozen: depositLaunching });

  const colorConfig = theme.whiteView;
  const { title, backgroundBtn, textColorBtn, dropzoneColors, bodyCopy } = colorConfig;
  const showBell = !originationDisabled;
  const [noticesOpen, setNoticesOpen] = useState(inboxOpen);
  const [preferencesOpen, setPreferencesOpen] = useState(preferencesOpenProp);
  const [readIds, setReadIds] = useState<string[]>([]);
  const [archivedIds, setArchivedIds] = useState<string[]>([]);
  const notices = GALLERY_NOTICE_FIXTURES.filter(
    (item) => !archivedIds.includes(item.id),
  ).map((item) => ({
    id: item.id,
    title: t(item.titleKey),
    body: t(item.bodyKey),
    isRead: item.read || readIds.includes(item.id),
    createdAt: item.createdAt,
  }));

  const items = useMemo<ITransaction[]>(
    () => {
      const mapped = deferredOriginationMockList.map((item) => {
        const launchId = item.operationId ?? item.originationId;
        return {
          ...item,
          launching:
            Boolean(item.cashDepositPending) &&
            (depositLaunching || payingOperationId === launchId),
          onDeposit: item.cashDepositPending
            ? () => {
                if (launchId) launchCashDeposit(launchId);
              }
            : undefined,
          onSelect: item.originationId
            ? () =>
                navigate(
                  `${DEFERRED_ORIGINATION_ROUTES.RECEIPT}/${item.originationId}`
                )
            : undefined,
        };
      });
      if (!depositLaunching) return mapped;
      return [
        ...mapped.filter((item) => item.cashDepositPending),
        ...mapped.filter((item) => !item.cashDepositPending),
      ];
    },
    [navigate, launchCashDeposit, payingOperationId, depositLaunching]
  );

  return (
    <>
    <AuraLayout
      colorConfig={colorConfig}
      className={
        showBell
          ? "need-help-header notifications-home-header"
          : "need-help-header"
      }
    >
      <Layout.Header>
        <Navbar
          title={showBell ? undefined : t("common.needHelp")}
          align="right"
          onBackClick={() => navigate(DEFERRED_ORIGINATION_ROUTES.LANDING)}
          onIconClick={showBell ? undefined : openSupport}
          icon={
            showBell ? (
              <GalleryInboxChrome
                ns="deferred-origination"
                unread={notices.filter((item) => !item.isRead).length}
                bellColor={title}
                badgeBackground={backgroundBtn}
                badgeTextColor={textColorBtn}
                onOpen={() => setNoticesOpen(true)}
              />
            ) : (
              <SupportIcon size={35} colors={dropzoneColors} />
            )
          }
        />
      </Layout.Header>
      <Layout.Content className="transactions-section">
        <TransactionsHistory items={items} />
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section transactions-footer">
          {originationDisabled && (
            <BodyCopy
              textColor={bodyCopy}
              className="transactions-disabled-note"
            >
              {t("transactions.transfersDisabled")}
            </BodyCopy>
          )}
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            disabled={originationDisabled}
            onClick={() => {
              if (!originationDisabled) {
                navigate(DEFERRED_ORIGINATION_ROUTES.TRANSFER);
              }
            }}
          >
            {t("transactions.newTransfer")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
    {supportSheet}
      {showBell ? (
        <>
        <GalleryInboxDrawer
          ns="deferred-origination"
          items={notices}
          open={noticesOpen}
          onClose={() => setNoticesOpen(false)}
          onMarkRead={(id) =>
            setReadIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
          }
          onArchive={(id) =>
            setArchivedIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
          }
          onOpenPreferences={() => {
            setNoticesOpen(false);
            setPreferencesOpen(true);
          }}
        />
        <GalleryPreferencesSheet
          ns="deferred-origination"
          open={preferencesOpen}
          onClose={() => setPreferencesOpen(false)}
        />
        </>
      ) : null}
      {toastOpen ? (
        <Toast message={t("notifications.exampleToast")} type="info" persist sound />
      ) : null}
  </>
  );
});

export const UserRestrictedHomePage = memo(() => (
  <HomePage originationDisabled />
));

export const InboxPage = memo(() => <HomePage inboxOpen />);

export const PreferencesPage = memo(() => <HomePage preferencesOpen />);

export const ToastPage = memo(() => <HomePage toastOpen />);

export const DepositLaunchingHomePage = memo(() => (
  <HomePage depositLaunching />
));
